import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Site-wide hook: when the URL contains a hash fragment, scroll to the
 * matching element and apply a slow "glow" animation to draw attention.
 *
 * The glow class is removed automatically after the animation completes
 * so it doesn't interfere with future navigations to the same hash.
 *
 * Requires the `animate-hash-glow` keyframe defined in index.css.
 */
export function useHashScrollGlow() {
  const { hash, key } = useLocation();

  useEffect(() => {
    if (!hash) return;

    const id = hash.replace("#", "");
    if (!id) return;

    // Small delay to let the target component mount (especially after navigation)
    const timer = setTimeout(() => {
      const el = document.getElementById(id);
      if (!el) return;

      el.scrollIntoView({ behavior: "smooth", block: "start" });

      // Apply glow after scroll settles
      const glowTimer = setTimeout(() => {
        el.classList.add("hash-glow");

        const cleanup = () => {
          el.classList.remove("hash-glow");
          el.removeEventListener("animationend", cleanup);
        };
        el.addEventListener("animationend", cleanup);

        // Safety fallback: remove after 5s even if animationend doesn't fire
        setTimeout(cleanup, 5000);
      }, 400);

      return () => clearTimeout(glowTimer);
    }, 350);

    return () => clearTimeout(timer);
  }, [hash, key]);
}
